import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { Card } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { DataTable, Column } from "../../components/common/Table";
import { Modal } from "../../components/common/Modal";
import { useAuth } from "../../context/AuthContext";
import type { Asset, Plant, Unit } from "../../types";

export default function AssetRegister() {
  const { hasPermission } = useAuth();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [plantFilter, setPlantFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Asset | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const loadLookups = async () => {
    const [p, u] = await Promise.all([api.get<Plant[]>("/api/master-data/plants"), api.get<Unit[]>("/api/master-data/units")]);
    setPlants(p);
    setUnits(u);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (plantFilter) params.set("plantId", plantFilter);
      if (unitFilter) params.set("unitId", unitFilter);
      const qs = params.toString();
      const rows = await api.get<Asset[]>(`/api/assets${qs ? `?${qs}` : ""}`);
      setAssets(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantFilter, unitFilter]);

  const unitsForPlantFilter = useMemo(() => (plantFilter ? units.filter((u) => u.plant_id === Number(plantFilter)) : units), [units, plantFilter]);

  const columns: Column<Asset>[] = [
    { header: "Tag No.", accessor: (a) => <span className="font-mono text-xs">{a.tag_no ?? a.asset_code}</span> },
    { header: "Description", accessor: (a) => a.asset_name },
    { header: "Equipment", accessor: (a) => a.machine_type ?? "-" },
    { header: "Plant", accessor: (a) => a.plant_name ?? "-" },
    { header: "Unit", accessor: (a) => a.unit_name ?? "-" },
    { header: "Maker", accessor: (a) => a.manufacturer ?? "-" },
    { header: "Criticality", accessor: (a) => <Badge value={a.criticality_level} /> },
    { header: "Status", accessor: (a) => <Badge value={a.current_status} /> },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Asset Register</h1>
          <p className="text-sm text-slate-500">Equipment list by Plant / Unit, with criticality and lifecycle tracking.</p>
        </div>
        {hasPermission("*", "asset:manage") && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New Equipment
          </button>
        )}
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap gap-2">
          <select
            className="input-field max-w-[200px]"
            value={plantFilter}
            onChange={(e) => {
              setPlantFilter(e.target.value);
              setUnitFilter("");
            }}
          >
            <option value="">All Plants</option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.plant_name}
              </option>
            ))}
          </select>
          <select className="input-field max-w-[200px]" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
            <option value="">All Units</option>
            {unitsForPlantFilter.map((u) => (
              <option key={u.id} value={u.id}>
                {u.unit_code} - {u.unit_name}
              </option>
            ))}
          </select>
          <input
            className="input-field max-w-xs"
            placeholder="Search tag, description, code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
          <button className="btn-secondary" onClick={load}>
            Search
          </button>
        </div>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        {loading ? (
          <p className="text-sm text-slate-400">Loading equipment…</p>
        ) : (
          <DataTable columns={columns} rows={assets} keyField={(a) => a.id} onRowClick={setSelected} />
        )}
      </Card>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.asset_name ?? ""} wide>
        {selected && (
          <div className="space-y-2 text-sm">
            <Row label="Tag No." value={selected.tag_no ?? "-"} />
            <Row label="Asset Code" value={selected.asset_code} />
            <Row label="Plant / Unit" value={`${selected.plant_name ?? "-"} / ${selected.unit_name ?? "-"}`} />
            <Row label="Equipment Type" value={selected.machine_type ?? "-"} />
            <Row label="Specification / Model" value={selected.specification ?? selected.model ?? "-"} />
            <Row label="Power (kW)" value={selected.power_kw != null ? `${selected.power_kw} kW` : "-"} />
            <Row label="Maker" value={selected.manufacturer ?? "-"} />
            <Row label="Serial Number" value={selected.serial_number ?? "-"} />
            <Row label="Department" value={selected.dept_name ?? "-"} />
            <Row label="Purchase Cost" value={`฿${selected.purchase_cost?.toLocaleString?.() ?? selected.purchase_cost}`} />
            <Row label="Expected Life" value={selected.expected_life_years ? `${selected.expected_life_years} years` : "-"} />
            <Row label="QR Code" value={selected.qr_code_value ?? "-"} />
            <Row label="Barcode" value={selected.barcode_value ?? "-"} />
            <div className="flex gap-2 pt-2">
              <Badge value={selected.criticality_level} />
              <Badge value={selected.current_status} />
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Equipment">
        <NewAssetForm
          plants={plants}
          units={units}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      </Modal>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between border-b border-slate-100 dark:border-industrial-700 py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function NewAssetForm({ plants, units, onCreated }: { plants: Plant[]; units: Unit[]; onCreated: () => void }) {
  const [form, setForm] = useState({
    plantId: "",
    unitId: "",
    tagNo: "",
    assetName: "",
    machineType: "",
    specification: "",
    powerKw: "",
    manufacturer: "",
    criticalityLevel: "MEDIUM",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const unitsForPlant = useMemo(() => (form.plantId ? units.filter((u) => u.plant_id === Number(form.plantId)) : []), [units, form.plantId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.unitId || !form.tagNo || !form.assetName) {
      setError("Unit, Tag No. and Description are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const unit = units.find((u) => u.id === Number(form.unitId));
      const prefixedTagNo = `${unit?.unit_code ?? "EQ"}-${form.tagNo}`.toUpperCase();
      await api.post("/api/assets", {
        assetCode: prefixedTagNo,
        assetName: form.assetName,
        unitId: Number(form.unitId),
        tagNo: prefixedTagNo,
        machineType: form.machineType || undefined,
        specification: form.specification || undefined,
        powerKw: form.powerKw ? Number(form.powerKw) : undefined,
        manufacturer: form.manufacturer || undefined,
        criticalityLevel: form.criticalityLevel,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create equipment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="grid grid-cols-2 gap-3">
        <select
          className="input-field"
          required
          value={form.plantId}
          onChange={(e) => setForm({ ...form, plantId: e.target.value, unitId: "" })}
        >
          <option value="">Select Plant</option>
          {plants.map((p) => (
            <option key={p.id} value={p.id}>
              {p.plant_name}
            </option>
          ))}
        </select>
        <select className="input-field" required disabled={!form.plantId} value={form.unitId} onChange={(e) => setForm({ ...form, unitId: e.target.value })}>
          <option value="">Select Unit</option>
          {unitsForPlant.map((u) => (
            <option key={u.id} value={u.id}>
              {u.unit_code} - {u.unit_name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <input className="input-field" placeholder="Tag No. (e.g. M10)" required value={form.tagNo} onChange={(e) => setForm({ ...form, tagNo: e.target.value })} />
        <p className="mt-1 text-xs text-slate-400">
          Will be saved as {form.unitId ? units.find((u) => u.id === Number(form.unitId))?.unit_code ?? "UNIT" : "UNIT"}-{form.tagNo || "TAG"}
        </p>
      </div>
      <input className="input-field" placeholder="Description" required value={form.assetName} onChange={(e) => setForm({ ...form, assetName: e.target.value })} />
      <input className="input-field" placeholder="Equipment Type (e.g. Motor, Valve)" value={form.machineType} onChange={(e) => setForm({ ...form, machineType: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <input className="input-field" placeholder="Specification / Model" value={form.specification} onChange={(e) => setForm({ ...form, specification: e.target.value })} />
        <input className="input-field" type="number" step="0.1" placeholder="Power (kW)" value={form.powerKw} onChange={(e) => setForm({ ...form, powerKw: e.target.value })} />
      </div>
      <input className="input-field" placeholder="Maker" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
      <select className="input-field" value={form.criticalityLevel} onChange={(e) => setForm({ ...form, criticalityLevel: e.target.value })}>
        <option value="HIGH">High Criticality</option>
        <option value="MEDIUM">Medium Criticality</option>
        <option value="LOW">Low Criticality</option>
      </select>
      <button type="submit" disabled={submitting} className="btn-primary w-full">
        {submitting ? "Creating…" : "Create Equipment"}
      </button>
    </form>
  );
}
